import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, Upload, Trash2, Download, FileText, AlertCircle, FileWarning,
  Clock, ShieldAlert, ChevronRight, Save, Skull,
} from "lucide-react";

const DOC_TYPES = ["Sözleşme", "Kimlik", "Ehliyet", "SRC", "Psikoteknik", "Sabıka Kaydı", "Diğer"];

function docStatus(iso) {
  if (!iso) return { color: "bg-slate-200 text-slate-500", label: "Girilmemiş" };
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const exp = new Date(iso);
  const days = Math.round((exp - today) / (1000 * 60 * 60 * 24));
  if (days < 0) return { color: "bg-red-100 text-red-700", label: `Süresi doldu (${-days} gün önce)` };
  if (days <= 30) return { color: "bg-amber-100 text-amber-800", label: `${days} gün kaldı` };
  return { color: "bg-emerald-100 text-emerald-700", label: `${days} gün geçerli` };
}

function fmtTl(n) {
  return `₺ ${Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function DriverProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [driver, setDriver] = useState(null);
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState({});
  const [docType, setDocType] = useState("Sözleşme");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/drivers/${id}`);
      setDriver(data);
      setEdit({
        phone: data.phone || "",
        role_type: data.role_type || "Şoför",
        license_expiry: data.license_expiry || "",
        src_expiry: data.src_expiry || "",
        psycho_expiry: data.psycho_expiry || "",
        note: data.note || "",
      });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Şoför yüklenemedi");
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const savePatch = async () => {
    try {
      const payload = {};
      ["phone", "role_type", "license_expiry", "src_expiry", "psycho_expiry", "note"].forEach((k) => {
        if ((edit[k] || "") !== (driver?.[k] || "")) payload[k] = edit[k];
      });
      if (Object.keys(payload).length === 0) return toast.info("Değişiklik yok");
      await api.patch(`/drivers/${id}`, payload);
      toast.success("Güncellendi");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kayıt başarısız");
    }
  };

  const uploadDoc = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("doc_type", docType);
      fd.append("file", file);
      await api.post(`/drivers/${id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Belge yüklendi");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Yükleme başarısız");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const downloadDoc = async (doc) => {
    try {
      const res = await api.get(`/drivers/${id}/documents/${doc.id}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: doc.content_type || "application/octet-stream" }));
      const a = document.createElement("a");
      a.href = url; a.download = doc.file_name || "belge"; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("İndirilemedi");
    }
  };

  const deleteDoc = async (doc) => {
    if (!window.confirm(`${doc.file_name || "Bu belge"} silinsin mi?`)) return;
    try {
      await api.delete(`/drivers/${id}/documents/${doc.id}`);
      toast.success("Silindi");
      await load();
    } catch (e) {
      toast.error("Silinemedi");
    }
  };

  if (busy && !driver) return <div className="p-8 text-slate-500">Yükleniyor...</div>;
  if (!driver) return null;

  const periods = driver.employment_periods || [];
  const monthly = driver.monthly || [];
  const violations = driver.violations || [];
  const accidents = driver.accidents || [];

  return (
    <div className="space-y-6 max-w-6xl mx-auto w-full" data-testid="driver-profile-page">
      <button className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-blue-700" onClick={() => navigate("/soforler")} data-testid="back-btn">
        <ArrowLeft className="w-4 h-4" /> Personel
      </button>

      {/* Header */}
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500 font-mono">Personel Profili · {driver.role_type || "Şoför"}</div>
              <h1 className="mt-1 font-heading font-bold text-2xl lg:text-3xl text-slate-900">{driver.name}</h1>
              <div className="mt-1 text-sm text-slate-600 font-mono">
                TC: {driver.tc ? `${driver.tc.slice(0,3)}****${driver.tc.slice(-4)}` : "—"} · Tel: {driver.phone || "—"}
              </div>
            </div>
            <div>
              {driver.status === "Aktif" ? (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 font-semibold" data-testid="status-badge">Aktif</span>
              ) : (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-slate-200 text-slate-600 font-semibold" data-testid="status-badge">Ayrıldı</span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Grid: left = timeline+docs; right = expiries+basic edit */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Timeline */}
        <Card className="lg:col-span-2" data-testid="timeline-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading font-bold flex items-center gap-2"><Clock className="w-4 h-4" /> Çalışma Dönemleri</h3>
              <div className="flex items-center gap-2">
                <Label className="text-xs text-slate-500">Görev</Label>
                <Select value={edit.role_type || driver.role_type || "Şoför"} onValueChange={(v) => setEdit({ ...edit, role_type: v })}>
                  <SelectTrigger className="h-8 w-[160px] text-xs" data-testid="edit-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Şoför", "Usta", "Ofis", "Saha Sorumlusu", "Diğer", "Belirtilmedi"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              {periods.length === 0 && <div className="text-sm text-slate-400">Kayıt yok</div>}
              {periods.map((p, i) => (
                <div key={i} className="border border-slate-200 rounded p-3 flex items-center gap-3 text-sm">
                  <div className="font-mono text-slate-700">
                    {p.start_date || "—"} <ChevronRight className="w-3 h-3 inline mx-1" /> {p.end_date || (p.leave_reason ? "—" : "Halen çalışıyor")}
                  </div>
                  {p.leave_reason && <span className="text-xs bg-slate-100 rounded px-2 py-0.5">{p.leave_reason}</span>}
                  {p.note && <span className="text-xs text-slate-500 italic truncate max-w-[200px]">"{p.note}"</span>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Belgeler tarih */}
        <Card data-testid="expiries-card">
          <CardContent className="p-5 space-y-3">
            <h3 className="font-heading font-bold flex items-center gap-2"><ShieldAlert className="w-4 h-4" /> Belge Bitiş Tarihleri</h3>
            {[
              ["license_expiry", "Ehliyet"],
              ["src_expiry", "SRC"],
              ["psycho_expiry", "Psikoteknik"],
            ].map(([k, lbl]) => {
              const st = docStatus(driver[k]);
              return (
                <div key={k}>
                  <Label className="text-xs">{lbl}</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input type="date" value={edit[k] || ""} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })} className="h-9 font-mono text-sm" data-testid={`edit-${k}`} />
                    <span className={`text-[10px] font-bold px-2 py-1 rounded-full whitespace-nowrap ${st.color}`}>{st.label}</span>
                  </div>
                </div>
              );
            })}
            <div className="pt-2">
              <Label className="text-xs">Telefon</Label>
              <Input value={edit.phone || ""} onChange={(e) => setEdit({ ...edit, phone: e.target.value })} className="h-9 font-mono text-sm mt-1" data-testid="edit-phone" />
            </div>
            <Button onClick={savePatch} className="w-full bg-[#1E3A8A] hover:bg-[#1e40af]" data-testid="save-edit-btn">
              <Save className="w-4 h-4 mr-1" /> Kaydet
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Documents */}
      <Card data-testid="documents-card">
        <CardContent className="p-5">
          <h3 className="font-heading font-bold flex items-center gap-2 mb-3"><FileText className="w-4 h-4" /> Özlük Evrakları</h3>
          <div className="flex flex-wrap items-end gap-3 mb-4">
            <div>
              <Label className="text-xs">Belge Türü</Label>
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger className="mt-1 h-10 w-[180px]" data-testid="doc-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DOC_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*,application/pdf"
              onChange={(e) => uploadDoc(e.target.files?.[0])}
              className="text-sm"
              data-testid="doc-file-input"
              disabled={uploading}
            />
            <Button onClick={() => fileRef.current?.click()} disabled={uploading} variant="outline" data-testid="doc-upload-btn">
              <Upload className="w-4 h-4 mr-1" /> {uploading ? "Yükleniyor..." : "Yükle"}
            </Button>
          </div>
          <div className="border rounded overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-3 py-2 text-xs uppercase tracking-widest text-slate-600">Tür</th>
                  <th className="text-left px-3 py-2 text-xs uppercase tracking-widest text-slate-600">Dosya</th>
                  <th className="text-left px-3 py-2 text-xs uppercase tracking-widest text-slate-600">Yükleyen</th>
                  <th className="text-left px-3 py-2 text-xs uppercase tracking-widest text-slate-600">Tarih</th>
                  <th className="text-right px-3 py-2 text-xs uppercase tracking-widest text-slate-600">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {(driver.documents || []).length === 0 && (
                  <tr><td colSpan={5} className="text-center py-6 text-slate-400">Henüz belge yüklenmemiş</td></tr>
                )}
                {(driver.documents || []).map((d) => (
                  <tr key={d.id} className="border-t" data-testid={`doc-row-${d.id}`}>
                    <td className="px-3 py-2 font-medium">{d.type}</td>
                    <td className="px-3 py-2 text-slate-700">{d.file_name}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{d.uploaded_by_name || "—"}</td>
                    <td className="px-3 py-2 font-mono text-xs">{(d.uploaded_at || "").slice(0, 10)}</td>
                    <td className="px-3 py-2 text-right">
                      <Button size="sm" variant="ghost" onClick={() => downloadDoc(d)} title="İndir" data-testid={`doc-download-${d.id}`}>
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteDoc(d)} title="Sil" data-testid={`doc-delete-${d.id}`}>
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Monthly bonus (12 ay) — sadece Şoför için */}
      {driver.role_type === "Şoför" && (
      <Card data-testid="monthly-card">
        <CardContent className="p-5">
          <h3 className="font-heading font-bold mb-3">Son 12 Ay — Puan &amp; Prim</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-3 py-2 text-xs uppercase text-slate-600">Ay</th>
                  <th className="text-center px-3 py-2 text-xs uppercase text-slate-600">Ceza</th>
                  <th className="text-center px-3 py-2 text-xs uppercase text-slate-600">Kalan Puan</th>
                  <th className="text-center px-3 py-2 text-xs uppercase text-slate-600">Çalışılan Gün</th>
                  <th className="text-right px-3 py-2 text-xs uppercase text-slate-600">Prim (TL)</th>
                </tr>
              </thead>
              <tbody>
                {monthly.map((m) => (
                  <tr key={m.month} className="border-t" data-testid={`month-${m.month}`}>
                    <td className="px-3 py-2 font-mono">{m.month}</td>
                    <td className="px-3 py-2 text-center">{m.penalty_total > 0 ? `−${m.penalty_total}` : "0"}</td>
                    <td className="px-3 py-2 text-center">{m.remaining_score}</td>
                    <td className="px-3 py-2 text-center font-mono text-xs">
                      {m.prorated ? `${m.worked_days} / ${m.days_in_month} gün` : <span className="text-slate-400">tam ay</span>}
                    </td>
                    <td className="px-3 py-2 text-right font-mono font-bold text-emerald-700">{fmtTl(m.bonus_tl)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Violations & Accidents grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card data-testid="violations-card">
          <CardContent className="p-5">
            <h3 className="font-heading font-bold flex items-center gap-2 mb-3"><AlertCircle className="w-4 h-4 text-amber-600" /> Disiplin İhlalleri ({violations.length})</h3>
            {violations.length === 0 ? (
              <div className="text-sm text-slate-400">Kayıt yok</div>
            ) : (
              <div className="space-y-2 max-h-[420px] overflow-y-auto">
                {violations.map((v) => (
                  <div key={v.id} className="border rounded p-2 text-sm" data-testid={`violation-${v.id}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-xs text-slate-500">{v.date}</span>
                      <span className="text-red-700 font-bold text-xs">−{v.points} pt</span>
                    </div>
                    <div className="text-slate-900">{v.type_label}</div>
                    {v.description && <div className="text-xs text-slate-500 italic mt-0.5 line-clamp-2">{v.description}</div>}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card data-testid="accidents-card">
          <CardContent className="p-5">
            <h3 className="font-heading font-bold flex items-center gap-2 mb-3"><FileWarning className="w-4 h-4 text-red-600" /> Kazalar / Bildirimler ({accidents.length})</h3>
            {accidents.length === 0 ? (
              <div className="text-sm text-slate-400">Kayıt yok</div>
            ) : (
              <div className="space-y-2 max-h-[420px] overflow-y-auto">
                {accidents.map((a) => (
                  <div key={a.id} className="border rounded p-2 text-sm" data-testid={`accident-${a.id}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-xs text-slate-500">{a.date} {a.time || ""}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                        a.status === "Açık" ? "bg-red-100 text-red-700" :
                        a.status === "Sigortada" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"
                      }`}>{a.status}</span>
                    </div>
                    <div className="text-slate-900">Plaka: {a.vehicle_plate || "—"} · Kusur: {a.fault}</div>
                    {a.description && <div className="text-xs text-slate-500 italic mt-0.5 line-clamp-2">{a.description}</div>}
                    <button className="text-blue-700 hover:underline text-xs mt-1" onClick={() => navigate(`/kaza-raporlari`)}>Rapora git →</button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Notes */}
      <Card data-testid="notes-card">
        <CardContent className="p-5">
          <h3 className="font-heading font-bold mb-2">Serbest Notlar</h3>
          <Textarea rows={4} value={edit.note || ""} onChange={(e) => setEdit({ ...edit, note: e.target.value })} data-testid="notes-textarea" />
          <div className="mt-2 flex justify-end">
            <Button onClick={savePatch} size="sm" className="bg-[#1E3A8A] hover:bg-[#1e40af]" data-testid="save-notes-btn">
              <Save className="w-4 h-4 mr-1" /> Notu Kaydet
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* DANGER ZONE — Kalıcı Silme (admin only) */}
      {isAdmin && <DangerZone driver={driver} onPurged={() => navigate("/soforler")} />}
    </div>
  );
}

// =============================================================
// Update 24 — Kalıcı silme
// =============================================================
function DangerZone({ driver, onPurged }) {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [confirmName, setConfirmName] = useState("");
  const [busy, setBusy] = useState(false);

  const openDialog = async () => {
    setConfirmName("");
    setPreview(null);
    setOpen(true);
    try {
      const { data } = await api.get(`/personnel/${driver.id}/purge-preview`);
      setPreview(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Önizleme alınamadı");
      setOpen(false);
    }
  };

  const doPurge = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/personnel/${driver.id}/purge`, { confirm_name: confirmName });
      const c = data.counts || {};
      toast.success(
        `Kalıcı olarak silindi. ${c.violations || 0} ihlal, ${c.shift_cells_cleared || 0} vardiya hücresi temizlendi, ${c.accidents_anonymized || 0} kaza raporunda şoför "Silinmiş personel" olarak işaretlendi.`
      );
      setOpen(false);
      if (onPurged) onPurged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Silinemedi");
    } finally { setBusy(false); }
  };

  const nameMatches = confirmName.trim().toUpperCase() === (driver?.name || "").trim().toUpperCase();
  const c = preview?.counts || {};

  return (
    <Card
      className="border-0"
      style={{ background: "rgba(255,92,92,0.06)", border: "1px solid rgba(255,92,92,0.4)" }}
      data-testid="danger-zone"
    >
      <CardContent className="p-5">
        <div className="flex items-start gap-4 flex-wrap">
          <Skull className="w-6 h-6 shrink-0" style={{ color: "var(--y-danger)" }} />
          <div className="flex-1 min-w-0">
            <h3
              className="font-heading font-bold"
              style={{ color: "var(--y-danger)", fontSize: 18 }}
            >
              Tehlikeli Bölge — Kalıcı Silme
            </h3>
            <p className="mt-1 text-sm" style={{ color: "var(--y-text-2)" }}>
              Bu kişinin personnel kaydı, özlük evrakları, disiplin ihlalleri, prim
              override kayıtları, belge uyarı bildirimleri ve vardiya çizelgesindeki
              hücreleri kalıcı olarak silinir. <b>Geri alınamaz.</b> Kaza raporları
              silinmez; ilgili şoför alanı <i>"Silinmiş personel"</i> olarak işaretlenir.
              Yeniden aynı TC ile Excel içe aktarma satırı otomatik atlanır.
            </p>
          </div>
          <Button
            onClick={openDialog}
            data-testid="danger-purge-btn"
            style={{
              background: "var(--y-danger)",
              color: "var(--y-accent-fg)",
              fontWeight: 700,
            }}
          >
            <Trash2 className="w-4 h-4 mr-2" /> Kalıcı Olarak Sil
          </Button>
        </div>
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="purge-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: "var(--y-danger)" }}>
              <ShieldAlert className="w-5 h-5" />
              Kalıcı silme onayı
            </DialogTitle>
          </DialogHeader>

          <div
            className="rounded-md p-3 text-sm"
            style={{
              background: "rgba(255,92,92,0.10)",
              border: "1px solid rgba(255,92,92,0.4)",
              color: "var(--y-text)",
            }}
          >
            Bu kişinin tüm kayıtları kalıcı olarak silinecek ve geri alınamaz.
          </div>

          {preview && (
            <div className="mt-3 space-y-1 text-sm" data-testid="purge-preview">
              <PreviewRow label="Özlük Evrakı" value={c.documents} />
              <PreviewRow label="Disiplin İhlali" value={c.violations} />
              <PreviewRow label="Prim Override" value={c.overrides} />
              <PreviewRow label="Vardiya Hücresi (satır silinmez, hücre boşalır)" value={c.shift_cells} />
              <PreviewRow label="Belge Uyarı Bildirimi" value={c.notifications} />
              {c.accidents > 0 && (
                <div
                  className="mt-2 rounded-md p-2 text-xs"
                  style={{ background: "rgba(242,201,76,0.10)", border: "1px solid rgba(242,201,76,0.4)", color: "var(--y-warning)" }}
                  data-testid="purge-accident-warning"
                >
                  ⚠ Bu kişi <b>{c.accidents}</b> kaza raporunda şoför olarak geçiyor. Rapor
                  silinmez; şoför alanı <b>"Silinmiş personel"</b> olarak değiştirilir.
                </div>
              )}
            </div>
          )}

          <div className="mt-4">
            <Label className="text-xs uppercase tracking-widest" style={{ color: "var(--y-label)" }}>
              Onaylamak için kişinin adını birebir yazın: <b style={{ color: "var(--y-text)" }}>{driver?.name}</b>
            </Label>
            <Input
              autoFocus
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              placeholder={driver?.name}
              className="mt-2 h-11"
              data-testid="purge-confirm-input"
            />
          </div>

          <div className="mt-4 flex justify-between gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)} data-testid="purge-cancel-btn">İptal</Button>
            <Button
              onClick={doPurge}
              disabled={!nameMatches || busy}
              data-testid="purge-confirm-btn"
              style={{
                background: nameMatches ? "var(--y-danger)" : "var(--y-surface-2)",
                color: nameMatches ? "var(--y-accent-fg)" : "var(--y-muted)",
                fontWeight: 700,
                opacity: nameMatches ? 1 : 0.6,
              }}
            >
              {busy ? "Siliniyor..." : "Kalıcı Olarak Sil"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function PreviewRow({ label, value }) {
  return (
    <div className="flex items-center justify-between px-2 py-1 rounded" style={{ background: "var(--y-surface-2)" }}>
      <span style={{ color: "var(--y-text-2)" }}>{label}</span>
      <span className="mono font-bold" style={{ color: "var(--y-text)" }}>{value ?? 0}</span>
    </div>
  );
}
